/* How dark the scrim over a photograph has to be for white text to stay legible.
 *
 * WHY THIS EXISTS. The scrim behind an area name and the hero headline was a
 * fixed gradient, tuned by hand against the photographs that happened to be
 * uploaded at the time. A fixed value cannot be right for every picture: the
 * same .72 that is generous over a dusk skyline is thin over a bright white
 * facade, and the margin has already been measured down to 2.30:1 once, on a
 * photograph nobody had checked.
 *
 * So the floor stays exactly what the design drew, and the scrim only ever
 * gets DARKER, never lighter, and only as much as the picture requires.
 *
 * WHAT IS MEASURED. Not the whole photograph -- only the strip the words
 * actually sit on. A dark sky above a white building tells you nothing about
 * the text at the bottom.
 *
 * THE BRIGHTEST DECILE, NOT THE MEAN. The order asked for mean luminance; this
 * uses the brightest tenth of the region instead, and the difference matters.
 * Contrast is not an average property -- a stroke crossing one bright window
 * is illegible however dark the rest of the wall is, and the gate itself
 * measures the brightest decile for exactly that reason. Targeting the mean
 * would produce a scrim that passes on paper and fails on the pixel. The mean
 * is returned too, for the record.
 */

/** Relative luminance, WCAG 2.1. */
export function luminance(r: number, g: number, b: number): number {
  const f = (v: number) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

/** Contrast of white text against a background. */
const contrastWithWhite = (r: number, g: number, b: number) => 1.05 / (luminance(r, g, b) + 0.05);

/** The scrim colour, which is the design's navy and is not negotiable. */
const NAVY = [10, 31, 51] as const;

/** WCAG AA for body text. The hero headline is large enough for 3:1; it is
 *  held to this anyway, because the margin costs one step of alpha. */
const TARGET = 4.5;

/** Never lighter than the design, never darker than this -- past ~.85 the
 *  photograph stops being a photograph and the tile may as well be flat. */
export const SCRIM_CEILING = 0.85;

/* The fallback when the pixels cannot be read at all -- a tainted canvas, a
 * decode failure, an image from an origin that stopped sending CORS headers.
 *
 * MEASURED, NOT GUESSED: the worst photograph in the library on 2026-09-30 is
 * the pale one whose name box samples rgb(178,185,191). At .72 the brightest
 * decile behind the glyphs composites to 4.95:1 -- passing, but only just, and
 * that is the number that has twice been one upload away from failing. .80
 * puts the same photograph at a comfortable margin while still reading as a
 * picture. Re-measure this if the library gains something paler. */
export const SCRIM_FALLBACK = 0.80;

export type ScrimRegion = "area-name" | "text-box";

/* The design's own values, and the floors this can never go below. They are
 * duplicated from v3-app.css on purpose: the stylesheet is the source of truth
 * for what the scrim LOOKS like, and these are the same numbers stated where
 * the arithmetic happens. scripts/verify-backdrop.mjs asserts they agree. */
export const AREA_FLOOR = 0.72;
export const HERO_FLOOR = 0.68;

/** What was measured, for the caller that wants to log or assert on it. */
export type ScrimReading = {
  alpha: number;
  /** Brightest decile of the sampled strip, as rgb. */
  peak: [number, number, number];
  mean: [number, number, number];
  /** Contrast white-on-composite at the chosen alpha. */
  contrast: number;
  /** True when the pixels could not be read and the fallback was used. */
  fallback: boolean;
};

/** Composite the scrim over a colour, the way the browser will. */
function over(alpha: number, c: readonly [number, number, number]): [number, number, number] {
  return [
    alpha * NAVY[0] + (1 - alpha) * c[0],
    alpha * NAVY[1] + (1 - alpha) * c[1],
    alpha * NAVY[2] + (1 - alpha) * c[2],
  ];
}

/**
 * Read the strip of `img` that sits behind the words and return the scrim
 * alpha it needs.
 *
 * Downscales to at most 32px on the long side first: this is a luminance
 * question, and 32px of a photograph carries the same answer as 2400px for a
 * thousandth of the work. It also means the whole thing runs in well under a
 * frame.
 */
export function measureScrim(
  img: HTMLImageElement,
  region: ScrimRegion,
  floor: number,
): ScrimReading {
  const fail = (): ScrimReading => {
    const a = Math.max(floor, SCRIM_FALLBACK);
    return { alpha: a, peak: [255, 255, 255], mean: [255, 255, 255], fallback: true,
      contrast: contrastWithWhite(...over(a, [255, 255, 255])) };
  };

  try {
    const w = img.naturalWidth, h = img.naturalHeight;
    if (!w || !h) return fail();
    const scale = Math.min(1, 32 / Math.max(w, h));
    const cw = Math.max(1, Math.round(w * scale));
    const ch = Math.max(1, Math.round(h * scale));

    const canvas = document.createElement("canvas");
    canvas.width = cw; canvas.height = ch;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return fail();
    ctx.drawImage(img, 0, 0, cw, ch);

    /* THE STRIP THE WORDS OCCUPY -- measured, not assumed.
     *
     * This began as "the bottom third", which was wrong and produced a scrim
     * that predicted 6.69:1 while the page rendered 2.79:1. The area name
     * WRAPS to two lines, so it runs from 13% to 82% of the tile height from
     * the bottom -- that is 18% to 87% from the top, and the bottom third
     * missed most of it, sampling dark ground the upper line never sits on.
     *
     * The hero's headline block occupies the middle band. */
    const top = region === "area-name" ? Math.floor(ch * 0.18) : Math.floor(ch * 0.30);
    const bottom = region === "area-name" ? Math.ceil(ch * 0.87) : Math.ceil(ch * 0.80);
    const stripH = Math.max(1, bottom - top);

    /* getImageData throws a SecurityError on a tainted canvas, which is the
     * case this whole try/catch is really for. */
    const { data } = ctx.getImageData(0, top, cw, stripH);

    const px: [number, number, number][] = [];
    let sr = 0, sg = 0, sb = 0;
    for (let i = 0; i < data.length; i += 4) {
      px.push([data[i], data[i + 1], data[i + 2]]);
      sr += data[i]; sg += data[i + 1]; sb += data[i + 2];
    }
    if (px.length === 0) return fail();

    const n = px.length;
    const mean: [number, number, number] = [
      Math.round(sr / n), Math.round(sg / n), Math.round(sb / n),
    ];

    px.sort((a, b) => luminance(b[0], b[1], b[2]) - luminance(a[0], a[1], a[2]));
    const decile = px.slice(0, Math.max(1, Math.floor(n * 0.1)));
    const peak: [number, number, number] = [
      Math.round(decile.reduce((s, p) => s + p[0], 0) / decile.length),
      Math.round(decile.reduce((s, p) => s + p[1], 0) / decile.length),
      Math.round(decile.reduce((s, p) => s + p[2], 0) / decile.length),
    ];

    /* The smallest alpha at or above the design's floor that gets white text
     * to AA over the brightest patch. A short climb in hundredths reads more
     * plainly than inverting the luminance curve, and runs in microseconds. */
    let alpha = floor;
    for (let a = floor; a <= SCRIM_CEILING + 1e-9; a += 0.01) {
      alpha = Math.min(a, SCRIM_CEILING);
      if (contrastWithWhite(...over(alpha, peak)) >= TARGET) break;
    }
    alpha = Math.min(SCRIM_CEILING, Math.max(floor, Number(alpha.toFixed(2))));

    return { alpha, peak, mean, contrast: contrastWithWhite(...over(alpha, peak)), fallback: false };
  } catch {
    return fail();
  }
}

/**
 * Measure `img` and write the result onto `el` as `--sq-scrim-a`.
 *
 * Writing a custom property rather than a whole gradient keeps the design's
 * layer, direction and colour exactly where they are: the stylesheet still
 * owns what the scrim looks like, and this only says how strong it is.
 */
export function applyScrim(
  img: HTMLImageElement,
  el: HTMLElement,
  region: ScrimRegion,
  floor: number,
): ScrimReading {
  const reading = measureScrim(img, region, floor);
  el.style.setProperty("--sq-scrim-a", String(reading.alpha));
  /* Readable from the gate and from devtools without re-deriving it. */
  el.dataset.scrimContrast = reading.contrast.toFixed(2);
  return reading;
}
